import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';

import { BaseSendEmail } from '~/components/common';
import { IEditNote } from '~/utils/interface/note';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleSendMail } from '~/thunks/customer/customerThunk';
import { ISendMail } from '~/utils/interface/mail';
import { handleGetJobFiles, handleGetTemplate } from '~/thunks/job/jobThunk';
import { jobActions } from '~/thunks/job/jobSlice';
import { IJob } from '~/utils/interface/job';

type SendMailDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IJob;
  id?: string;
};

const initialValues: ISendMail = {
  note: '',
  files: undefined,
  to: '',
  subject: '',
  customerFileIds: [],
  cc: '',
  bcc: '',
  from: '',
};

const SendMailDialog = (props: SendMailDialogProps) => {
  //#region Destructuring Props

  // //#endregion Destructuring Props
  // //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/component', 'admin/common']);
  const { needRefreshData } = useAppSelector((state) => state.customer);
  //#endregion Declare Hook
  useAppSelector((state) => state.customer);
  //#region Selector
  //#endregion Selector
  //#region Declare State

  const [dataSendEmail, setDataSendEmail] = useState<IEditNote>();
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetTemplate({ params: { type: 'email' } }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);

  useEffect(() => {
    form.setFieldValue('jobId', cast(props.id));
    form.setFieldValue('note', cast(dataSendEmail?.note));
    form.setFieldValue('to', cast(dataSendEmail?.to));
    form.setFieldValue('subject', cast(dataSendEmail?.subject));
    form.setFieldValue('cc', cast(dataSendEmail?.cc));
    form.setFieldValue('bcc', cast(dataSendEmail?.bcc));
    form.setFieldValue('from', cast(dataSendEmail?.from));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, needRefreshData, dispatch, dataSendEmail]);

  useEffect(() => {
    dispatch(handleGetJobFiles({ id: props.id, data: { limit: 0 } }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, props.id]);

  //#endregion Implement Hook
  // //#region Handle Function
  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(handleSendMail(values)).unwrap();
        notify('success', t('sendMailSuccess', { ns: 'admin/common' }));
        dispatch(jobActions.changeNeedRefreshData(true));
        handleSetOpen(false);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
      return;
    },
  });
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('sendMail')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <BaseSendEmail
          onChange={(value) => {
            setDataSendEmail(value);
          }}
          formik={form}
          fileType='job'
          jobId={props.id}
          value={{ to: cast(props.data?.customer?.email) }}
        />
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(SendMailDialog);
